import { Link, useLocation } from "react-router-dom";
import { cn } from "@jambushrushti/lib/utils";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Globe,
  Image,
  FileText,
  PenTool,
  Calendar,
  GraduationCap,
  Search,
  Building,
  Brain,
  Key,
  ClipboardList,
  Settings,
  LogOut,
  Shield,
  X,
  Newspaper,
  Share2,
  MessageSquare,
  Video,
  Ticket,
} from "lucide-react";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@jambushrushti/components/ui/avatar";

const navGroups = [
  {
    groupLabel: "MAIN OVERVIEW",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", href: "/admin/dashboard" },
      { icon: Users, label: "Users & Roles", href: "/admin/users" },
      { icon: GraduationCap, label: "Scholars", href: "/admin/scholars" },
    ],
  },
  {
    groupLabel: "KNOWLEDGE & CONTENT",
    items: [
      { icon: BookOpen, label: "Guruvani", href: "/admin/guruvani" },
      { icon: Globe, label: "Website CMS", href: "/admin/cms" },
      { icon: FileText, label: "Books (PDFs)", href: "/admin/books" },
      {
        icon: Newspaper,
        label: "Articles & Tributes",
        href: "/admin/articles",
      },
      { icon: PenTool, label: "Blog Posts", href: "/admin/blog" },
      { icon: FileText, label: "eMagazine", href: "/admin/emagazine" },
      { icon: Image, label: "Media Gallery", href: "/admin/gallery" },
    ],
  },
  {
    groupLabel: "COMMUNITY & ENGAGEMENT",
    items: [
      { icon: Calendar, label: "Events", href: "/admin/events" },
      { icon: Video, label: "Live Telecast", href: "/admin/live-telecast" },
      { icon: Share2, label: "Social Media", href: "/admin/social-media" },
      {
        icon: MessageSquare,
        label: "Visitor Feedback",
        href: "/admin/feedback",
      },
      { icon: Ticket, label: "Ticket Management", href: "/tickets/dashboard" },
    ],
  },
  {
    groupLabel: "ECOSYSTEM & SYSTEM",
    items: [
      { icon: Search, label: "Research Tools", href: "/admin/research" },
      { icon: Building, label: "Digital Museum", href: "/admin/museum" },
      { icon: Brain, label: "AI & Indexing", href: "/admin/ai" },
      { icon: Key, label: "Access Requests", href: "/admin/access-requests" },
      { icon: ClipboardList, label: "Audit Logs", href: "/admin/logs" },
      { icon: Settings, label: "System Settings", href: "/admin/settings" },
    ],
  },
];

interface AdminSidebarProps {
  onClose?: () => void;
}

export function AdminSidebar({ onClose }: AdminSidebarProps) {
  const location = useLocation();
  const { user, logout } = useAdminAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/admin";
  };

  return (
    <aside className="shadow-xs sticky top-0 z-30 flex h-screen w-72 flex-col border-r border-amber-900/10 bg-white text-stone-800 lg:w-64">
      {/* Logo/Brand */}
      <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-900/10 bg-amber-50/40 p-4 sm:p-5">
        <Link
          to="/admin/dashboard"
          className="flex items-center gap-3"
          onClick={onClose}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-amber-600 to-amber-500 shadow-md shadow-amber-500/20">
            <Shield className="h-4.5 w-4.5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-base font-bold leading-tight tracking-tight text-stone-900">
              Admin Console
            </h1>
            <p className="text-[11px] font-semibold tracking-wide text-amber-700">
              Jambushrushti Portal
            </p>
          </div>
        </Link>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="text-stone-500 hover:bg-stone-100 hover:text-stone-900 lg:hidden"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* User Info */}
      <div className="flex-shrink-0 border-b border-amber-900/10 bg-amber-50/20 p-3.5">
        <div className="flex items-center gap-3">
          <Avatar className="shadow-xs h-9 w-9 border-2 border-amber-500/30">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-amber-100 text-xs font-bold text-amber-800">
              {user?.name?.charAt(0).toUpperCase() || "A"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-bold text-stone-900">
              {user?.name || "Administrator"}
            </p>
            <p className="truncate text-[11px] text-stone-500">
              {user?.email || "admin@jambushrushti.org"}
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="flex-shrink-0 border-b border-amber-900/10 p-3 lg:hidden">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search console..."
            className="w-full rounded-lg border border-stone-200 bg-stone-100/70 py-1.5 pl-8 pr-3 text-xs text-stone-900 placeholder:text-stone-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Categorized Navigation - Independent Smooth Scroll */}
      <nav className="custom-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
        {navGroups.map(group => (
          <div key={group.groupLabel} className="space-y-1">
            <h3 className="px-3 text-[10px] font-bold uppercase tracking-wider text-stone-400">
              {group.groupLabel}
            </h3>
            {group.items.map(item => {
              const isActive =
                location.pathname === item.href ||
                (item.href !== "/admin/dashboard" &&
                  location.pathname.startsWith(item.href));

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onClose}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl px-3 py-2 text-xs font-medium transition-all duration-150",
                    isActive
                      ? "bg-gradient-to-r from-amber-600 to-amber-500 font-semibold text-white shadow-md shadow-amber-500/20"
                      : "text-stone-600 hover:bg-amber-50/60 hover:text-stone-900"
                  )}
                >
                  <item.icon
                    className={cn(
                      "h-4 w-4 flex-shrink-0",
                      isActive ? "text-white" : "text-stone-400"
                    )}
                  />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Logout */}
      <div className="flex-shrink-0 border-t border-amber-900/10 bg-amber-50/20 p-3">
        <Button
          variant="ghost"
          className="h-9 w-full justify-start rounded-xl text-xs text-stone-600 hover:bg-rose-50 hover:text-rose-700"
          onClick={handleLogout}
        >
          <LogOut className="mr-2.5 h-4 w-4 text-stone-400" />
          Sign Out Console
        </Button>
      </div>
    </aside>
  );
}
